import {
  ComparisonEntity,
  DateFormat,
  DropdownOption,
  EntityType,
  FilterElement,
  MultiSelectOption,
  TableFilter,
  TagColor,
  TagProps,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

export enum InquiryFilterTypes {
  DATE_RANGE,
  MIN_MAX,
  PROVIDER,
  SELECT,
  STATUS,
}

export type InquiryFilterValue =
  | FilterElement[]
  | FilterElement
  | string
  | number
  | boolean;

export const INQUIRY_DATE_MIN_LIMIT_IN_MONTHS = 6;

export enum InquiryFilterKeys {
  CHANNEL = 'channel',
  CHANNEL_TYPE = 'channel-type',
  LAST_CONTACT_DATE_MIN = 'last-contact-date-min',
  LAST_CONTACT_DATE_MAX = 'last-contact-date-max',
  INQUIRY_STATUS = 'inquiry-status',
  TIMEZONE = 'timezone',
  INQUIRY_DATE_MIN = 'inquiry-date-min',
  INQUIRY_DATE_MAX = 'inquiry-date-max',
  TARGET_TREATMENT_PROGRAM = 'target-treatment-program',
}

export enum PSRFilterKeys {
  ADMISSIONS_SUPERVISOR_EXTERNAL_ID = 'admissions-supervisor-external-id',
  INFORMATION_SOURCE = 'information-source',
  PSR_EXTERNAL_ID = 'psr-external-id',
  RC_EXTERNAL_ID = 'rc-external-id',
  IS_QC_SCHEDULED = 'is-qc-scheduled',
}

export enum ASFilterKeys {
  AS_EXTERNAL_ID = 'as-external-id',
  AO_EXTERNAL_ID = 'ao-external-id',
  INSURANCE_REVIEW_STATUS = 'insurance-company-review-status',
  NEXT_CONTACT_DATE_MIN = 'next-contact-date-start',
  NEXT_CONTACT_DATE_MAX = 'next-contact-date-end',
  TARGET_ADMIT_DATE_MIN = 'target-admit-date-min',
  TARGET_ADMIT_DATE_MAX = 'target-admit-date-max',
  MEDICAL_REVIEW_STATUS = 'medical-clearance-status',
  MEDICAL_APPOINTMENT_START_DATE = 'medical-appointment-date-min',
  MEDICAL_APPOINTMENT_END_DATE = 'medical-appointment-date-max',
  INSURANCE_COMPANY = 'insurance-company-external-id',
}

export type InquiryPSRFilterKeys = PSRFilterKeys | InquiryFilterKeys;
export type InquiryASFilterKeys = ASFilterKeys | InquiryFilterKeys;

interface InquiryFilterBaseConfig {
  defaultValue?: InquiryFilterValue;
  key: InquiryPSRFilterKeys | InquiryASFilterKeys;
  label: string;
  placeholder?: string;
}

type MinMaxFilterConfigObject = {
  min: InquiryFilterBaseConfig;
  max: InquiryFilterBaseConfig;
};

export interface InquiryFilterConfig extends InquiryFilterBaseConfig {
  isSingleSelect?: boolean;
  minMax?: MinMaxFilterConfigObject;
  options?: MultiSelectOption[];
  providerFilterData?: ProviderFilterData;
  type: InquiryFilterTypes;
}

const InquirySharedFilterNameMapper: Record<InquiryFilterKeys, string> = {
  [InquiryFilterKeys.CHANNEL]: 'Channel',
  [InquiryFilterKeys.CHANNEL_TYPE]: 'Channel Type',
  [InquiryFilterKeys.LAST_CONTACT_DATE_MIN]: 'Days since last contact min',
  [InquiryFilterKeys.LAST_CONTACT_DATE_MAX]: 'Days since last contact max',
  [InquiryFilterKeys.INQUIRY_STATUS]: 'Status',
  [InquiryFilterKeys.TIMEZONE]: 'Timezone',
  [InquiryFilterKeys.INQUIRY_DATE_MIN]: 'Created start date',
  [InquiryFilterKeys.INQUIRY_DATE_MAX]: 'Created end date',
  [InquiryFilterKeys.TARGET_TREATMENT_PROGRAM]: 'Admitting Treatment Program',
};

export const InquiryPSRFilterNameMapper: Record<InquiryPSRFilterKeys, string> =
  {
    ...InquirySharedFilterNameMapper,
    [PSRFilterKeys.ADMISSIONS_SUPERVISOR_EXTERNAL_ID]: 'Admissions Supervisor',
    [PSRFilterKeys.PSR_EXTERNAL_ID]: 'Patient Service Representative',
    [PSRFilterKeys.RC_EXTERNAL_ID]: 'Referral Coordinator',
    [PSRFilterKeys.INFORMATION_SOURCE]: 'Mode of inquiry',
    [PSRFilterKeys.IS_QC_SCHEDULED]: 'QC appointment scheduled?',
  };

export const InquiryASFilterNameMapper: Record<InquiryASFilterKeys, string> = {
  ...InquirySharedFilterNameMapper,
  [ASFilterKeys.AO_EXTERNAL_ID]: 'Admissions Operations Coordinator',
  [ASFilterKeys.INSURANCE_REVIEW_STATUS]: 'Insurance review status',
  [ASFilterKeys.NEXT_CONTACT_DATE_MIN]: 'Next contact start date',
  [ASFilterKeys.NEXT_CONTACT_DATE_MAX]: 'Next contact end date',
  [ASFilterKeys.TARGET_ADMIT_DATE_MIN]: 'Target admit start date',
  [ASFilterKeys.TARGET_ADMIT_DATE_MAX]: 'Target admit end date',
  [ASFilterKeys.MEDICAL_REVIEW_STATUS]: 'Medical clearance status',
  [ASFilterKeys.MEDICAL_APPOINTMENT_START_DATE]:
    'Medical appointment start date',
  [ASFilterKeys.MEDICAL_APPOINTMENT_END_DATE]: 'Medical appointment end date',
  [ASFilterKeys.INSURANCE_COMPANY]: 'Insurance company',
  [ASFilterKeys.AS_EXTERNAL_ID]: 'Admissions Specialist',
};

export enum TextInputV2Type {
  DATE = 'date format',
  EMAIL = 'email',
  NUMBER = 'number',
  PASSWORD = 'password',
  PHONE = 'phone',
  PHONE_CODE = 'phone code',
  TEXT = 'text',
}

export const EquipInquiryChannelNameValue: Record<
  EquipInquiryChannelName,
  EquipInquiryChannelName
> = {
  Direct: 'Direct',
  Referral: 'Referral',
};

export const EquipInquirerRoleCategoryCodeValue: Record<
  EquipInquirerRoleCategoryCode,
  EquipInquirerRoleCategoryCode
> = {
  PLO: 'PLO',
  REF: 'REF',
};
export enum FieldLength {
  date = 10,
}
export const INQUIRY_CLOSED_STATUS_LABEL = 'Closed';

export const channelTypeMenuOptions: DropdownOption[] = [
  {
    label: 'Payor referral',
    value: 'payor-referral',
  },
  {
    label: 'Partner referral',
    value: 'partner-referral',
  },
  {
    label: 'Provider referral',
    value: 'provider-referral',
  },
];

export const UnassignedUniversalSelectOption = {
  label: 'Unassigned',
  value: 'none',
};

export enum InquiryStatusValue {
  ASSESSING = 'Assessing',
  FIELDED = 'Fielded',
  NEW = 'New',
  QUALIFIED = 'Qualified',
  ONBOARDING = 'Onboarding',
  ENROLLED = 'Enrolled',
  CLOSED = 'Closed',
  UNKNOWN = 'Unknown',
}

export enum MedicalStatusOptions {
  NOT_STARTED = 'Not started',
  APPT_CONFIRMED = 'Appt confirmed',
  CONTACT_ESTABLISHED = 'Contact established',
  READY_FOR_REVIEW = 'Ready for review',
  IN_PROGRESS = 'In progress',
  CLEARED = 'Cleared',
  NOT_CLEARED = 'Not cleared',
  EXPIRED = 'Expired',
}

export enum InsuranceReviewStatusOptions {
  PREAUTHORIZATION_IN_PROGRESS = 'Pre-auth in progress',
  NOT_READY = 'Not ready',
  READY_FOR_REVIEW = 'Ready for review',
  VERIFICATION_IN_PROGRESS = 'Verification in progress',
  VERIFICATION_COMPLETE = 'Verification complete',
  APPROVED = 'Approved',
  DENIED = 'Denied',
}

export enum InquiryViewType {
  PSR = 'psr',
  AS = 'as',
}

export const InquiryViewOptions = {
  AS: {
    code: InquiryViewType.AS,
    name: 'Admissions Specialist',
  },
  PSR: {
    code: InquiryViewType.PSR,
    name: 'Patient Service Representative',
  },
};

export const DefaultPSRInquiryFilters: TableFilter[] = [
  {
    filterKey: InquiryFilterKeys.INQUIRY_STATUS,
    filters: [
      { label: InquiryStatusValue.NEW, value: InquiryStatusValue.NEW },
      { label: InquiryStatusValue.FIELDED, value: InquiryStatusValue.FIELDED },
    ],
    title: InquiryPSRFilterNameMapper[InquiryFilterKeys.INQUIRY_STATUS],
  },
  {
    filterKey: PSRFilterKeys.PSR_EXTERNAL_ID,
    filters: [
      {
        /*
        Intentionally adding white space so it matches the value in the UniversalSelect component
        that is trying to join a first and last name resulting in a white space between the two.
        This should't be necessary and Universal Select with Provider Search should be able to handle this case.
        To be handled  https://teamequip.atlassian.net/browse/PT-786
        */
        label: `${UnassignedUniversalSelectOption.label} `,
        value: UnassignedUniversalSelectOption.value,
      },
    ],
    title: InquiryPSRFilterNameMapper[PSRFilterKeys.PSR_EXTERNAL_ID],
  },
];

export const DefaultASInquiryFilters: TableFilter[] = [];

export const OTHER_PAYOR = 'Other';

export const InquiryStatusDropdownMenuOptions: TagProps[] = [
  {
    color: TagColor.marigold,
    id: 'tag-dropdown-menu__new',
    value: InquiryStatusValue.NEW,
  },
  {
    color: TagColor.periwinkle,
    id: 'tag-dropdown-menu__fielded',
    value: InquiryStatusValue.FIELDED,
  },
  {
    color: TagColor.purple,
    id: 'tag-dropdown-menu__qualified',
    value: InquiryStatusValue.QUALIFIED,
  },
  {
    color: TagColor.orange,
    id: 'tag-dropdown-menu__assessing',
    value: InquiryStatusValue.ASSESSING,
  },
  {
    color: TagColor.pink,
    id: 'tag-dropdown-menu__onboarding',
    value: InquiryStatusValue.ONBOARDING,
  },
  {
    color: TagColor.green,
    id: 'tag-dropdown-menu__enrolled',
    value: InquiryStatusValue.ENROLLED,
  },
  {
    color: TagColor.red,
    id: 'tag-dropdown-menu__closed',
    value: InquiryStatusValue.CLOSED,
  },
];

export const InquiryChannelNameValueColorMap: Record<
  EquipInquiryChannelName,
  string
> = {
  Direct: TagColor.marigold,
  Referral: TagColor.periwinkle,
};

export const channelOptions: ChannelProps[] = [
  {
    channel: EquipInquiryChannelNameValue.Direct,
    color: InquiryChannelNameValueColorMap[EquipInquiryChannelNameValue.Direct],
  },
  {
    channel: EquipInquiryChannelNameValue.Referral,
    color:
      InquiryChannelNameValueColorMap[EquipInquiryChannelNameValue.Referral],
  },
];

const subtractDaysAgoAndFormatDate = (days: number, date = new Date()) => {
  if (!days) return DateTime.fromJSDate(date).toFormat(DateFormat.longDate);
  return DateTime.fromJSDate(date).minus({ days }).toISODate();
};

export const getInquiryDate = (
  dateStr: string,
  filterKey:
    | InquiryFilterKeys.INQUIRY_DATE_MIN
    | InquiryFilterKeys.INQUIRY_DATE_MAX
    | ASFilterKeys.NEXT_CONTACT_DATE_MIN
    | ASFilterKeys.NEXT_CONTACT_DATE_MAX
    | ASFilterKeys.TARGET_ADMIT_DATE_MIN
    | ASFilterKeys.TARGET_ADMIT_DATE_MAX
    | ASFilterKeys.MEDICAL_APPOINTMENT_START_DATE
    | ASFilterKeys.MEDICAL_APPOINTMENT_END_DATE,
) => {
  const convertedDate = DateTime.fromISO(dateStr, { zone: 'utc' });
  let value: string;

  // Even though Inquiry and Next Contact Date are dates in the backend, the composition service expects
  // a full timestamp.
  if (
    filterKey === InquiryFilterKeys.INQUIRY_DATE_MIN ||
    filterKey === ASFilterKeys.NEXT_CONTACT_DATE_MIN
  ) {
    value = convertedDate.toISO();
  } else if (
    filterKey === InquiryFilterKeys.INQUIRY_DATE_MAX ||
    filterKey === ASFilterKeys.NEXT_CONTACT_DATE_MAX
  ) {
    value = convertedDate.endOf('day').toISO();
  } else {
    value = convertedDate.toISODate();
  }

  return [
    {
      label: convertedDate.toFormat(DateFormat.longDate),
      value,
    },
  ];
};

export const filterValuesMapper: Record<
  InquiryFilterKeys | PSRFilterKeys | ASFilterKeys,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (filters: any) => { filters: FilterElement[]; title: string }
> = {
  [InquiryFilterKeys.INQUIRY_STATUS]: (filters: FilterElement[]) => ({
    filters,
    title: InquiryPSRFilterNameMapper[InquiryFilterKeys.INQUIRY_STATUS],
  }),
  [PSRFilterKeys.ADMISSIONS_SUPERVISOR_EXTERNAL_ID]: (
    filters: FilterElement[],
  ) => ({
    filters: filters.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ({ userId, value }: any) => ({
        label: value,
        value: userId,
      }),
    ),
    title:
      InquiryPSRFilterNameMapper[
        PSRFilterKeys.ADMISSIONS_SUPERVISOR_EXTERNAL_ID
      ],
  }),
  [PSRFilterKeys.PSR_EXTERNAL_ID]: (filters: FilterElement[]) => ({
    filters: filters.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ({ userId, value }: any) => ({
        label: value,
        value: userId,
      }),
    ),
    title: InquiryPSRFilterNameMapper[PSRFilterKeys.PSR_EXTERNAL_ID],
  }),
  [PSRFilterKeys.RC_EXTERNAL_ID]: (filters: FilterElement[]) => ({
    filters: filters.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ({ userId, value }: any) => ({
        label: value,
        value: userId,
      }),
    ),
    title: InquiryPSRFilterNameMapper[PSRFilterKeys.RC_EXTERNAL_ID],
  }),
  [InquiryFilterKeys.LAST_CONTACT_DATE_MIN]: (filter: string) => ({
    filters: [
      {
        label: subtractDaysAgoAndFormatDate(Number(filter)),
        value: filter,
      },
    ],
    title: InquiryPSRFilterNameMapper[InquiryFilterKeys.LAST_CONTACT_DATE_MIN],
  }),
  [InquiryFilterKeys.LAST_CONTACT_DATE_MAX]: (filter: string) => ({
    filters: [
      {
        label: subtractDaysAgoAndFormatDate(Number(filter)),
        value: filter,
      },
    ],
    title: InquiryPSRFilterNameMapper[InquiryFilterKeys.LAST_CONTACT_DATE_MAX],
  }),
  [InquiryFilterKeys.TIMEZONE]: (filters: FilterElement[]) => ({
    filters,
    title: InquiryPSRFilterNameMapper[InquiryFilterKeys.TIMEZONE],
  }),
  [PSRFilterKeys.IS_QC_SCHEDULED]: (filter: FilterElement) => ({
    filters: [
      {
        label: filter.label,
        value: filter.value === 'Scheduled',
      },
    ],
    title: InquiryPSRFilterNameMapper[PSRFilterKeys.IS_QC_SCHEDULED],
  }),
  [InquiryFilterKeys.CHANNEL]: (filters: FilterElement[]) => ({
    filters,
    title: InquiryPSRFilterNameMapper[InquiryFilterKeys.CHANNEL],
  }),
  [InquiryFilterKeys.CHANNEL_TYPE]: (filters: FilterElement[]) => ({
    filters,
    title: InquiryPSRFilterNameMapper[InquiryFilterKeys.CHANNEL_TYPE],
  }),
  [PSRFilterKeys.INFORMATION_SOURCE]: (filters: FilterElement[]) => ({
    filters,
    title: InquiryPSRFilterNameMapper[PSRFilterKeys.INFORMATION_SOURCE],
  }),
  [InquiryFilterKeys.INQUIRY_DATE_MIN]: (filter: string) => ({
    filters: getInquiryDate(filter, InquiryFilterKeys.INQUIRY_DATE_MIN),
    title: InquiryPSRFilterNameMapper[InquiryFilterKeys.INQUIRY_DATE_MIN],
  }),
  [InquiryFilterKeys.INQUIRY_DATE_MAX]: (filter: string) => ({
    filters: getInquiryDate(filter, InquiryFilterKeys.INQUIRY_DATE_MAX),
    title: InquiryPSRFilterNameMapper[InquiryFilterKeys.INQUIRY_DATE_MAX],
  }),
  [ASFilterKeys.AO_EXTERNAL_ID]: (filters: FilterElement[]) => ({
    filters: filters.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ({ userId, value }: any) => ({
        label: value,
        value: userId,
      }),
    ),
    title: InquiryASFilterNameMapper[ASFilterKeys.AO_EXTERNAL_ID],
  }),
  [ASFilterKeys.INSURANCE_REVIEW_STATUS]: (filters: FilterElement[]) => ({
    filters,
    title: InquiryASFilterNameMapper[ASFilterKeys.INSURANCE_REVIEW_STATUS],
  }),
  [ASFilterKeys.NEXT_CONTACT_DATE_MIN]: (filter: string) => ({
    filters: getInquiryDate(filter, ASFilterKeys.NEXT_CONTACT_DATE_MIN),
    title: InquiryASFilterNameMapper[ASFilterKeys.NEXT_CONTACT_DATE_MIN],
  }),
  [ASFilterKeys.NEXT_CONTACT_DATE_MAX]: (filter: string) => ({
    filters: getInquiryDate(filter, ASFilterKeys.NEXT_CONTACT_DATE_MAX),
    title: InquiryASFilterNameMapper[ASFilterKeys.NEXT_CONTACT_DATE_MAX],
  }),
  [ASFilterKeys.TARGET_ADMIT_DATE_MIN]: (filter: string) => ({
    filters: getInquiryDate(filter, ASFilterKeys.TARGET_ADMIT_DATE_MIN),
    title: InquiryASFilterNameMapper[ASFilterKeys.TARGET_ADMIT_DATE_MIN],
  }),
  [ASFilterKeys.TARGET_ADMIT_DATE_MAX]: (filter: string) => ({
    filters: getInquiryDate(filter, ASFilterKeys.TARGET_ADMIT_DATE_MAX),
    title: InquiryASFilterNameMapper[ASFilterKeys.TARGET_ADMIT_DATE_MAX],
  }),
  [ASFilterKeys.MEDICAL_REVIEW_STATUS]: (filters: FilterElement[]) => ({
    filters,
    title: InquiryASFilterNameMapper[ASFilterKeys.MEDICAL_REVIEW_STATUS],
  }),
  [ASFilterKeys.MEDICAL_APPOINTMENT_START_DATE]: (filter: string) => ({
    filters: getInquiryDate(
      filter,
      ASFilterKeys.MEDICAL_APPOINTMENT_START_DATE,
    ),
    title:
      InquiryASFilterNameMapper[ASFilterKeys.MEDICAL_APPOINTMENT_START_DATE],
  }),
  [ASFilterKeys.MEDICAL_APPOINTMENT_END_DATE]: (filter: string) => ({
    filters: getInquiryDate(filter, ASFilterKeys.MEDICAL_APPOINTMENT_END_DATE),
    title: InquiryASFilterNameMapper[ASFilterKeys.MEDICAL_APPOINTMENT_END_DATE],
  }),
  [ASFilterKeys.INSURANCE_COMPANY]: (filters: FilterElement[]) => ({
    filters,
    title: InquiryASFilterNameMapper[ASFilterKeys.INSURANCE_COMPANY],
  }),
  [ASFilterKeys.AS_EXTERNAL_ID]: (filters: FilterElement[]) => ({
    filters: filters.map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ({ userId, value }: any) => ({
        label: value,
        value: userId,
      }),
    ),
    title: InquiryASFilterNameMapper[ASFilterKeys.AS_EXTERNAL_ID],
  }),
  [InquiryFilterKeys.TARGET_TREATMENT_PROGRAM]: (filters: FilterElement[]) => ({
    filters,
    title:
      InquirySharedFilterNameMapper[InquiryFilterKeys.TARGET_TREATMENT_PROGRAM],
  }),
};

const getInitialFilterElements = (
  filterKey: InquiryPSRFilterKeys | InquiryASFilterKeys,
  defaultFilters: TableFilter[] = DefaultPSRInquiryFilters,
) =>
  defaultFilters.find((filter: TableFilter) => filter.filterKey === filterKey);

const sharedInitialFilterElementMapper = (
  defaultFilters: TableFilter[],
): Record<InquiryFilterKeys, InquiryFilterValue> => {
  return {
    [InquiryFilterKeys.INQUIRY_STATUS]: getInitialFilterElements(
      InquiryFilterKeys.INQUIRY_STATUS,
      defaultFilters,
    )?.filters as FilterElement[],
    [InquiryFilterKeys.LAST_CONTACT_DATE_MIN]: getInitialFilterElements(
      InquiryFilterKeys.LAST_CONTACT_DATE_MIN,
      defaultFilters,
    )?.filters[0]?.value,
    [InquiryFilterKeys.LAST_CONTACT_DATE_MAX]: getInitialFilterElements(
      InquiryFilterKeys.LAST_CONTACT_DATE_MAX,
      defaultFilters,
    )?.filters[0]?.value,
    [InquiryFilterKeys.TIMEZONE]: getInitialFilterElements(
      InquiryFilterKeys.TIMEZONE,
      defaultFilters,
    )?.filters,
    [InquiryFilterKeys.CHANNEL]: getInitialFilterElements(
      InquiryFilterKeys.CHANNEL,
      defaultFilters,
    )?.filters,
    [InquiryFilterKeys.CHANNEL_TYPE]: getInitialFilterElements(
      InquiryFilterKeys.CHANNEL_TYPE,
      defaultFilters,
    )?.filters,
    [InquiryFilterKeys.INQUIRY_DATE_MIN]: getInitialFilterElements(
      InquiryFilterKeys.INQUIRY_DATE_MIN,
      defaultFilters,
    )?.filters[0]?.value,
    [InquiryFilterKeys.INQUIRY_DATE_MAX]: getInitialFilterElements(
      InquiryFilterKeys.INQUIRY_DATE_MAX,
      defaultFilters,
    )?.filters[0]?.value,
    [InquiryFilterKeys.TARGET_TREATMENT_PROGRAM]: getInitialFilterElements(
      InquiryFilterKeys.TARGET_TREATMENT_PROGRAM,
      defaultFilters,
    )?.filters,
  };
};

const mapInitialFilterElements = (elements: TableFilter) =>
  elements?.filters.map(({ label, value }) => ({
    label,
    userId: value,
    value: label,
  }));

export const PSRInitialFilterElementMapper = (
  defaultFilters: TableFilter[],
): Record<InquiryPSRFilterKeys, InquiryFilterValue> => {
  return {
    ...sharedInitialFilterElementMapper(defaultFilters),
    [PSRFilterKeys.ADMISSIONS_SUPERVISOR_EXTERNAL_ID]:
      getInitialFilterElements(
        PSRFilterKeys.ADMISSIONS_SUPERVISOR_EXTERNAL_ID,
        defaultFilters,
      )?.filters.map(({ label, value }) => ({
        label,
        userId: value,
        value: label,
      })),
    [PSRFilterKeys.PSR_EXTERNAL_ID]: getInitialFilterElements(
      PSRFilterKeys.PSR_EXTERNAL_ID,
      defaultFilters,
    )?.filters.map(({ label, value }) => ({
      label,
      userId: value,
      value: label,
    })),
    [PSRFilterKeys.RC_EXTERNAL_ID]: getInitialFilterElements(
      PSRFilterKeys.RC_EXTERNAL_ID,
      defaultFilters,
    )?.filters.map(({ label, value }) => ({
      label,
      userId: value,
      value: label,
    })),
    [PSRFilterKeys.IS_QC_SCHEDULED]: getInitialFilterElements(
      PSRFilterKeys.IS_QC_SCHEDULED,
      defaultFilters,
    )?.filters.map(({ label }) => ({
      label,
      value: label,
    }))[0],
    [PSRFilterKeys.INFORMATION_SOURCE]: getInitialFilterElements(
      PSRFilterKeys.INFORMATION_SOURCE,
      defaultFilters,
    )?.filters.map(({ label, value }) => ({
      id: value,
      label,
      value: label,
    })),
  };
};

export const ASInitialFilterElementMapper = (
  defaultFilters: TableFilter[],
): Record<InquiryASFilterKeys, InquiryFilterValue> => {
  return {
    ...sharedInitialFilterElementMapper(defaultFilters),
    [ASFilterKeys.AO_EXTERNAL_ID]: mapInitialFilterElements(
      getInitialFilterElements(ASFilterKeys.AO_EXTERNAL_ID, defaultFilters),
    ),
    [ASFilterKeys.INSURANCE_REVIEW_STATUS]: getInitialFilterElements(
      ASFilterKeys.INSURANCE_REVIEW_STATUS,
      defaultFilters,
    )?.filters,
    [ASFilterKeys.NEXT_CONTACT_DATE_MIN]: getInitialFilterElements(
      ASFilterKeys.NEXT_CONTACT_DATE_MIN,
      defaultFilters,
    )?.filters[0]?.value,
    [ASFilterKeys.NEXT_CONTACT_DATE_MAX]: getInitialFilterElements(
      ASFilterKeys.NEXT_CONTACT_DATE_MAX,
      defaultFilters,
    )?.filters[0]?.value,
    [ASFilterKeys.TARGET_ADMIT_DATE_MIN]: getInitialFilterElements(
      ASFilterKeys.TARGET_ADMIT_DATE_MIN,
      defaultFilters,
    )?.filters[0]?.value,
    [ASFilterKeys.TARGET_ADMIT_DATE_MAX]: getInitialFilterElements(
      ASFilterKeys.TARGET_ADMIT_DATE_MAX,
      defaultFilters,
    )?.filters[0]?.value,
    [ASFilterKeys.MEDICAL_REVIEW_STATUS]: getInitialFilterElements(
      ASFilterKeys.MEDICAL_REVIEW_STATUS,
      defaultFilters,
    )?.filters,
    [ASFilterKeys.MEDICAL_APPOINTMENT_START_DATE]: getInitialFilterElements(
      ASFilterKeys.MEDICAL_APPOINTMENT_START_DATE,
      defaultFilters,
    )?.filters[0]?.value,
    [ASFilterKeys.MEDICAL_APPOINTMENT_END_DATE]: getInitialFilterElements(
      ASFilterKeys.MEDICAL_APPOINTMENT_END_DATE,
      defaultFilters,
    )?.filters[0]?.value,
    [ASFilterKeys.INSURANCE_COMPANY]: getInitialFilterElements(
      ASFilterKeys.INSURANCE_COMPANY,
      defaultFilters,
    )?.filters,
    [ASFilterKeys.AS_EXTERNAL_ID]: mapInitialFilterElements(
      getInitialFilterElements(ASFilterKeys.AS_EXTERNAL_ID, defaultFilters),
    ),
  };
};

export enum MergeConfirmationActionType {
  MERGE = 'merge',
  KEEP = 'keep',
  CLOSE = 'close',
}

export const familyContactMergeFields = new Set([
  'email',
  'cellPhone',
  'chosenName',
  'firstName',
  'lastName',
  'languages',
  'pronouns',
  'timezone',
  'isPrimaryContact',
  'relationToPatient',
]);

export const nonFamilyContactMergeFields = new Set([
  'address',
  'email',
  'officePhone',
  'officeExtension',
  'faxNumber',
  'firstName',
  'lastName',
  'prefix',
  'isOngoingProvider',
  'isPrimaryContact',
  'relationToPatient',
]);

export const coverageMergeFields = new Set([
  'payor',
  'subscriberId',
  'groupId',
  'stateCode',
  'startDate',
  'endDate',
  'providerServicesPhoneNumber',
  'patientRelationToSubscriber',
  'alternativeId',
  'customPayorName',
  'coveragePriority',
  'isInNetwork',
  'subscriberFirstName',
  'subscriberLastName',
  'subscriberDateOfBirth',
  'coveragePriority',
]);

export const COMPARISON_FIELD_LABELS: Record<string, string> = {
  address: 'Address',
  alternativeId: 'Alternative ID',
  cellPhone: 'Cell phone',
  chosenName: 'Chosen name',
  coverageExternalId: 'Coverage external ID',
  coveragePriority: 'Coverage priority',
  createdAt: 'Created at',
  createdBy: 'Created by',
  customPayorName: 'Custom payor name',
  email: 'Email',
  endDate: 'End date',
  faxNumber: 'Fax Number',
  firstName: 'First name',
  groupId: 'Group ID',
  inquiryId: 'Inquiry ID',
  isActive: 'Is active',
  isInNetwork: 'Is in network',
  isOngoingProvider: 'Is ongoing provider',
  isPrimaryContact: 'Is primary contact',
  languages: 'Languages',
  lastName: 'Last name',
  officeExtension: 'Office extension',
  officePhone: 'Office phone',
  patientExternalId: 'Patient external ID',
  patientRelationToSubscriber: 'Patient relation to subscriber',
  payor: 'Payor',
  payorExternalId: 'Payor external ID',
  prefix: 'Prefix',
  pronouns: 'Pronouns',
  providerServicesPhoneNumber: 'Provider services phone number',
  relationToPatient: 'Relation to patient',
  startDate: 'Start date',
  stateCode: 'State code',
  subscriberDateOfBirth: 'Subscriber date of birth',
  subscriberFirstName: 'Subscriber first name',
  subscriberId: 'Subscriber ID',
  subscriberLastName: 'Subscriber last name',
  timezone: 'Timezone',
  updatedAt: 'Updated at',
  updatedBy: 'Updated by',
};

export type BaseComparisonEntity = ComparisonEntity & {
  entityType: EntityType;
};

export type CoverageComparisonEntity = BaseComparisonEntity & {
  coverageExternalId?: string;
};

export type ContactComparisonEntity = BaseComparisonEntity & {
  contactExternalId?: string;
  supportExternalId?: string;
};

export type EitherComparisonEntity =
  | ContactComparisonEntity
  | CoverageComparisonEntity;

export type NormalizeDuplicateContactResponseData = {
  baseEntity: ContactComparisonEntity;
  matchedEntities: ContactComparisonEntity[];
};

export type NormalizeDuplicateCoverageResponseData = {
  baseEntity: CoverageComparisonEntity;
  matchedEntities: CoverageComparisonEntity[];
};

export type NormalizeDuplicateResponseData =
  | NormalizeDuplicateContactResponseData
  | NormalizeDuplicateCoverageResponseData;
